// App-construction step. Was previously inline in main.ts. Returns the Vue
// app so the bootstrap pipeline can mount it after all init steps have run.

import { createApp, type App as VueApp } from 'vue'
import { createPinia } from 'pinia'
import { VueQueryPlugin } from '@tanstack/vue-query'
import PrimeVue from 'primevue/config'
import Tooltip from 'primevue/tooltip'
import 'primeicons/primeicons.css'
import 'primeflex/primeflex.css'
import * as Sentry from '@sentry/vue'
import VueTelInput from 'vue-tel-input'

import App from '@app/App.vue'
import router from '@app/router'
import { config } from '@core/config/config'
import { queryClient } from '@core/query/queryClient'
import '@shared/adapters/chartjs'
import SlicealPreset from '@uiKit/theme/slicealPreset'
import '@uiKit/theme/fonts.css'
import '@uiKit/theme/main.css'

export const createAppInstance = (): VueApp => {
  const app = createApp(App)
  const pinia = createPinia()

  app.use(pinia)
  pinia.use(Sentry.createSentryPiniaPlugin())
  app.use(router)
  app.use(VueQueryPlugin, { queryClient })

  // Dev-only: expose the QueryClient on `window.__qc` so Playwright and
  // browser-console flows can poke the cache to simulate server states
  // (e.g. dashboard state-matrix verification). Stripped in production
  // because `config.isDev` resolves to a static `false` at build.
  if (config.isDev) {
    ;(window as unknown as { __qc?: unknown }).__qc = queryClient
  }

  app.use(VueTelInput)
  app.use(PrimeVue, {
    theme: {
      preset: SlicealPreset,
      options: {
        prefix: 'p',
        darkModeSelector: '.p-dark',
        cssLayer: false,
      },
    },
  })
  app.directive('tooltip', Tooltip)

  return app
}

export { router }
